/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  Bell,
  Calendar,
  CalendarPlus,
  CheckCircleSolid,
  ChatBubble,
  Mail,
  NavArrowDown,
  OpenInWindow,
  Page,
  PhoneIncome,
  PhoneOutcome,
  Reply,
  Trash,
  UserBadgeCheck,
  Xmark,
  Group,
} from 'iconoir-react'
import classNames from 'classnames'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { RichTextEditor } from '../editor/RichTextEditor'
import { CallInitiationModal } from '../click-to-call/CallInitiationModal'
import { OutboundCallProvider } from '../click-to-call/OutboundCallProvider'
import { LazyContactCell } from '../LazyContactCell'
import { LazyStaffCell } from '../LazyStaffCell'
import { TaskAssigneeLabel } from 'app/components/tasks/TaskAssigneeLabel'
import { assigneeInput } from 'lib/taskAssignee'
import CompaniesIcon from '../Icon/CompaniesIcon'
import { InlineAsyncPicker } from '../shared/InlineAsyncPicker'
import NameIcon from '../shared/NameIcon'
import { Badge } from '../ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu'
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import CalendarPicker from '../marketing/CalendarPicker'
import { useConfirmation } from '../ConfirmationContext'
import type { ContactJson, TaskJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifySuccess } from 'app/services/notify.client'
import { assigneesFetcher } from 'app/lib/fetchers/assigneesFetcher'
import { gqlFetchList, gqlFetchOne } from 'app/lib/graphqlClient'
import { dueDateAsDay } from 'app/lib/taskDueDate'
import { bg } from 'lib/background'
import { log } from 'lib/logging'
import { digitsOnly, usPhoneNumber } from 'lib/phone'
import { formatPhoneNumber } from 'app/formatPhoneNumber'

export type TaskFieldInput = Partial<{
  title: string
  description: string | null
  dueDate: string
  contactId: string
  ownerId: string | null
  roleId: string | null
  status: 'open' | 'closed'
}>

export type OnTaskUpdate = (input: TaskFieldInput) => Promise<boolean>

// ── Inline-edit primitives ───────────────────────────────────────────────────
// All share the same shape: click to enter edit mode, Enter to save, Escape to
// cancel, blur saves. Optimistic update is handled by the parent's onUpdate.

function InlineText({
  value,
  onSave,
  className,
  placeholder = `—`,
}: {
  value: string
  onSave: (next: string) => void | Promise<unknown>
  className?: string
  placeholder?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  // Auto-grow the textarea to match the wrapped content so editing a long
  // multi-line title doesn't collapse the box to a single row. Runs whenever
  // the draft changes (or we enter edit mode).
  useEffect(() => {
    if (!editing) return
    const el = textareaRef.current
    if (!el) return
    el.style.height = `auto`
    el.style.height = `${el.scrollHeight}px`
  }, [editing, draft])

  const commit = async () => {
    const trimmed = draft.trim()
    if (trimmed && trimmed !== value) await onSave(trimmed)
    setEditing(false)
  }

  if (editing) {
    return (
      <textarea
        ref={(el) => {
          textareaRef.current = el
          if (el) {
            // Place caret at the end and grow to fit on first paint.
            el.focus()
            el.setSelectionRange(el.value.length, el.value.length)
            el.style.height = `auto`
            el.style.height = `${el.scrollHeight}px`
          }
        }}
        rows={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === `Enter` && !e.shiftKey) {
            // Single-line semantics: Enter commits, no newline inserted.
            e.preventDefault()
            commit()
          }
          if (e.key === `Escape`) {
            setDraft(value)
            setEditing(false)
          }
        }}
        className={classNames(
          `-mx-2 -my-1 block w-full resize-none overflow-hidden rounded border border-info-default bg-surface-raised px-2 py-1 outline-none focus:ring-2 focus:ring-info-default/30`,
          className,
        )}
      />
    )
  }
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => {
        if (e.key === `Enter`) setEditing(true)
      }}
      className={classNames(
        `-mx-2 -my-1 cursor-text rounded px-2 py-1 transition-colors hover:bg-surface-muted/60`,
        className,
      )}
    >
      {value || <span className="text-minimal">{placeholder}</span>}
    </div>
  )
}

function InlineDate({
  value,
  display,
  onSave,
  className,
}: {
  value?: string
  display: React.ReactNode
  onSave: (iso: string) => void | Promise<unknown>
  className?: string
}) {
  const [open, setOpen] = useState(false)

  // Floating popover anchored to the display cell. `flip` lets it pop up
  // when there isn't room below; the portal keeps it on top of the Sheet
  // drawer rather than getting clipped by overflow boundaries.
  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: `bottom-start`,
    middleware: [offset(6), flip({ padding: 10 })],
    whileElementsMounted: autoUpdate,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floating.context),
    useDismiss(floating.context),
  ])

  const handleDateChange = (next: Date) => {
    // Send noon-local as ISO. The server truncates dueDate to "calendar day
    // in UTC", so the time-of-day we send only matters in that it has to
    // land on the intended date in UTC. Noon-local does that for any
    // timezone between UTC-11 and UTC+12 (essentially anywhere people live).
    const iso = moment(next).startOf(`day`).add(12, `hours`).toISOString()
    // For change detection, normalize the original the same way (date-only
    // portion) so we don't fire a no-op save when re-picking the same day.
    const original = value ? moment.utc(value).format(`YYYY-MM-DD`) : ``
    const nextDay = moment(next).format(`YYYY-MM-DD`)
    if (nextDay !== original) bg(Promise.resolve(onSave(iso)))
    setOpen(false)
  }

  return (
    <>
      <div
        ref={floating.refs.setReference}
        role="button"
        tabIndex={0}
        {...getReferenceProps({
          onKeyDown: (e) => {
            if (e.key === `Enter`) setOpen(true)
          },
        })}
        className={classNames(
          `-mx-2 -my-1 cursor-pointer rounded px-2 py-1 transition-colors hover:bg-surface-muted/60`,
          className,
        )}
      >
        {display}
      </div>
      {open && (
        <FloatingPortal>
          <div
            ref={floating.refs.setFloating}
            style={floating.floatingStyles}
            {...getFloatingProps()}
            className="z-50 h-fit w-80 rounded-lg border border-default bg-surface-raised p-2 shadow-elevation-2"
          >
            <CalendarPicker
              // Parse the stored value as a date-only moment (UTC YYYY-MM-DD →
              // local moment at that date) so the calendar opens on the
              // intended day, not yesterday/tomorrow due to TZ shift.
              date={value ? dueDateAsDay(value).format() : undefined}
              onDateChange={handleDateChange}
              limitToMonth={false}
              allowPastDates
              allowFutureDates
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

// Always-editable rich text for the task description, so the editor is permanently
// visible (no hidden affordance) and commits via a debounced save as the user types. The Lexical editor is initialized
// once per task (via the `key` prop on mount) so cursor position survives saves.
//
// Exported for unit testing (autosave timing against parent re-renders).
export function AutoSaveRichText({
  value,
  onSave,
  placeholder,
  namespace,
}: {
  value: string
  onSave: (next: string) => void | Promise<unknown>
  placeholder?: string
  namespace: string
}) {
  const [draft, setDraft] = useState(value)
  const lastSavedRef = useRef(value)

  // Keep the latest onSave in a ref so the debounce effect does not depend on
  // its identity. The callers pass an inline arrow (`(next) => onUpdate(...)`),
  // so onSave changes identity on every parent render. The detail pane
  // re-renders often while the user types (contact hydration, refresh ticks),
  // and when the save effect depended on onSave each such re-render cleared and
  // rescheduled the 800ms timer, so a steady stream of re-renders stopped the
  // save from ever firing — the description change never reached the server.
  const onSaveRef = useRef(onSave)
  useEffect(() => {
    onSaveRef.current = onSave
  }, [onSave])

  useEffect(() => {
    setDraft(value)
    lastSavedRef.current = value
  }, [value])

  // Debounced autosave — 800ms after the last keystroke. Skip if unchanged from
  // the last server-confirmed value so we don't fire on every Lexical update.
  // Depends only on `draft` (via the ref for onSave), so parent re-renders can
  // no longer reset the timer.
  useEffect(() => {
    if (draft === lastSavedRef.current) return
    const timer = setTimeout(() => {
      if (draft === lastSavedRef.current) return
      lastSavedRef.current = draft
      onSaveRef.current(draft)
    }, 800)
    return () => clearTimeout(timer)
  }, [draft])

  // Flush any pending edit when the editor unmounts (task switch, drawer close)
  // so a change typed within the last 800ms is not lost. The flush reads the
  // draft through a ref and runs on unmount only: with `draft` as a dependency
  // its cleanup ran on every keystroke and saved each previous draft, and the
  // echo of those saves reset the draft, so the final text was never saved.
  const draftRef = useRef(draft)
  useEffect(() => {
    draftRef.current = draft
  }, [draft])
  useEffect(() => {
    return () => {
      if (draftRef.current !== lastSavedRef.current) {
        lastSavedRef.current = draftRef.current
        onSaveRef.current(draftRef.current)
      }
    }
  }, [])

  return (
    <RichTextEditor
      namespace={namespace}
      text={value}
      placeholder={placeholder}
      editable
      autoFocus={false}
      onChange={(text) => setDraft(text)}
    />
  )
}

// Contact info card shown inline in the task detail pane. The point is to give the
// person actually doing the task ("Follow up with Johnny") enough patient context to
// act — phone, email, lead status, last activity — without opening the contact drawer.
export type ContactInfo = {
  id: string
  name?: string | null
  email?: string | null
  cellPhone?: string | null
  homePhone?: string | null
  workPhone?: string | null
  leadStatus?: string | null
  lifecycleStage?: string | null
  lastActivityDate?: string | null
  lastAppointmentDate?: string | null
  nearestAppointmentDate?: string | null
  lastHygieneDate?: string | null
  locationId?: string | null
}

const CONTACT_FIELDS = `id name email cellPhone homePhone workPhone leadStatus lifecycleStage lastActivityDate lastAppointmentDate nearestAppointmentDate lastHygieneDate locationId`

function formatPhone(p?: string | null): string | null {
  if (!p) return null
  const us = usPhoneNumber(p)
  if (!us) return p
  // A stored +1 number keeps its prefix on screen, as it always has here.
  return digitsOnly(p).length === 11 ? `+1 ${formatPhoneNumber(us)}` : (formatPhoneNumber(us) ?? p)
}

function formatRelativeDate(d?: string | null): string | null {
  if (!d) return null
  const m = moment(d)
  const today = moment().startOf(`day`)
  const diff = m.startOf(`day`).diff(today, `days`)
  if (diff === 0) return `Today`
  if (diff === -1) return `Yesterday`
  if (diff === 1) return `Tomorrow`
  if (diff < 0 && diff > -7) return `${Math.abs(diff)}d ago`
  if (diff > 0 && diff < 7) return `In ${diff}d`
  if (m.year() === moment().year()) return m.format(`MMM D`)
  return m.format(`MMM D, YYYY`)
}

type ActivityItem = {
  id: string
  type: string
  subType?: string | null
  activityDate?: string | null
  createdDate?: string | null
  description?: string | null
  note?: string | null
  owner?: { name?: string | null } | null
  // Resolved follow-up outcome (only populated for FollowUp activities once the
  // corresponding FollowUp record is fetched). Mirrors the `isConnected` /
  // `isScheduled` / `appointmentDate` shape on FollowUpGql so we can render
  // chips like "Connected · Scheduled Mar 12" in the timeline row.
  followUpOutcome?: {
    isConnected?: boolean | null
    isScheduled?: boolean | null
    appointmentDate?: string | null
  } | null
}

const ACTIVITY_FIELDS = `id type subType activityDate createdDate description note owner { name }`

// Icon + colour for each activity type, matching the contact-drawer pattern
// (h-5 w-5 rounded-md p-1 with a coloured bg, on-color icon). The neutral
// fallbacks (note, default) use the bg-fill-neutral role instead of a
// raw dark gray, so the tile stays dark under the white icon in both themes.
function activityVisual(type: string): { Icon: typeof Bell; bg: string; text: string } {
  const t = (type || ``).toLowerCase()
  if (t.includes(`outbound`)) return { Icon: PhoneOutcome, bg: `bg-blue-600`, text: `text-on-color` }
  if (t.includes(`call`)) return { Icon: PhoneIncome, bg: `bg-blue-600`, text: `text-on-color` }
  if (t.includes(`sms`) || t.includes(`chat`) || t.includes(`message`))
    return { Icon: ChatBubble, bg: `bg-green-600`, text: `text-on-color` }
  if (t.includes(`email`)) return { Icon: Mail, bg: `bg-purple-600`, text: `text-on-color` }
  // Match the canonical follow-up treatment used by `FollowUpCard` in the
  // contact activity feed: Reply icon on a green-600 background.
  if (t.includes(`follow`)) return { Icon: Reply, bg: `bg-green-600`, text: `text-on-color` }
  if (t.includes(`task`)) return { Icon: CheckCircleSolid, bg: `bg-amber-500`, text: `text-on-color` }
  if (t.includes(`appointment`) || t.includes(`scheduler`))
    return { Icon: CalendarPlus, bg: `bg-green-600`, text: `text-on-color` }
  if (t.includes(`patient`)) return { Icon: UserBadgeCheck, bg: `bg-emerald-600`, text: `text-on-color` }
  if (t.includes(`note`)) return { Icon: Page, bg: `bg-fill-neutral`, text: `text-on-color` }
  return { Icon: Bell, bg: `bg-fill-neutral`, text: `text-on-color` }
}

// Categorize an activity into one of three logical buckets. Returns null for types
// we deliberately don't surface here (system events, etc.).
type ActivityCategory = 'follow-ups' | 'communications' | 'notes'
function activityCategory(type: string): ActivityCategory | null {
  const t = (type || ``).toLowerCase()
  if (t.includes(`call`) || t.includes(`sms`) || t.includes(`chat`) || t.includes(`message`) || t.includes(`email`))
    return `communications`
  if (t.includes(`note`)) return `notes`
  if (t.includes(`task`) || t.includes(`follow`) || t.includes(`scheduler`) || t.includes(`appointment`))
    return `follow-ups`
  return null
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const { Icon, bg, text } = activityVisual(item.type)
  const date = item.activityDate || item.createdDate
  const summary = item.description?.trim() || item.note?.trim()
  const outcome = item.followUpOutcome
  return (
    <li className="flex gap-3">
      <Icon className={classNames(`h-5 w-5 shrink-0 rounded-md p-1`, bg, text)} strokeWidth={2} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
        <div className="flex items-center justify-between gap-2 text-body-xs">
          <span className="font-medium text-strong">
            {item.type}
            {item.subType ? <span className="ml-1 font-normal text-subtle">· {item.subType}</span> : null}
          </span>
          {date && <span className="shrink-0 tabular-nums text-subtle">{formatRelativeDate(date)}</span>}
        </div>
        {summary && (
          // Quoted-snippet treatment: tinted card + left accent bar are
          // enough on their own to signal "this is excerpted content" — no
          // italics, no pseudo-element quote mark. Text stays in the same
          // weight as the rest of the row so longer summaries read cleanly.
          <blockquote className="line-clamp-3 rounded-r border-l-2 border-strong bg-surface-muted/60 px-2.5 py-1.5 text-body-sm text-default">
            {summary}
          </blockquote>
        )}
        {/* Follow-up outcome chips. `isConnected` and `isScheduled` are the
            two outcome flags persisted on the FollowUp record; when scheduled
            we also surface the appointment date so the row gives the full
            picture without expanding. */}
        {outcome && (
          <div className="mt-0.5 flex flex-wrap items-center gap-1">
            <Badge
              color={outcome.isConnected ? `green` : `gray`}
              label={outcome.isConnected ? `Connected` : `Not connected`}
              icon={
                <span
                  className={classNames(
                    `h-1.5 w-1.5 rounded-full`,
                    outcome.isConnected ? `bg-green-600` : `bg-(--fg-minimal)`,
                  )}
                />
              }
            />
            {outcome.isScheduled && (
              <Badge
                color="blue"
                label={
                  outcome.appointmentDate
                    ? `Scheduled · ${moment(outcome.appointmentDate).format(`MMM D`)}`
                    : `Scheduled`
                }
                icon={<CalendarPlus className="h-3 w-3" strokeWidth={2} />}
              />
            )}
          </div>
        )}
        {item.owner?.name && <span className="text-body-xs text-minimal">by {item.owner.name}</span>}
      </div>
    </li>
  )
}

function ContactActivityTimeline({ contactId }: { contactId: string }) {
  const [items, setItems] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  // Map of Call activity id → human-readable AI summary, fetched separately because
  // Activity.data is null for calls; the summary lives on the Call record's aiSummary.
  const [callSummaries, setCallSummaries] = useState<Record<string, string>>({})
  // Map of FollowUp activity id → outcome flags. Activity rows for follow-ups
  // don't carry the connected/scheduled bits inline, so we look them up on the
  // associated FollowUp record once the timeline lands.
  const [followUpOutcomes, setFollowUpOutcomes] = useState<
    Record<string, { isConnected?: boolean | null; isScheduled?: boolean | null; appointmentDate?: string | null }>
  >({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    bg(
      gqlFetchList<ActivityItem>(
        `activities`,
        {
          // Filter on the nested `contacts.id` field (Activity → Contact[]).
          // Exclude system records (`Change`, `Patient`) — those are field-diff /
          // lifecycle events, not real interactions someone needs context on.
          search: `contacts.id:${contactId} AND NOT type:Change AND NOT type:Patient`,
          pagination: { page: 0, limit: 20 },
          sort: { field: `activityDate`, direction: `DESC` },
        },
        ACTIVITY_FIELDS,
      ).then(({ hits }) => {
        if (cancelled) return
        setItems(hits ?? [])
        setLoading(false)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [contactId])

  // After activities load, parallel-fetch aiSummary for each Call. Capped at the top
  // 5 calls so we don't fire 20 requests for a comm-heavy contact.
  useEffect(() => {
    const callActivities = items.filter((it) => it.type === `Call`).slice(0, 5)
    if (callActivities.length === 0) return
    let cancelled = false
    bg(
      Promise.all(
        callActivities.map((a) => {
          const callId = a.id.startsWith(`Call-`) ? a.id.slice(5) : a.id
          return gqlFetchOne<{ id: string; ai?: { summary?: string } }>(`call`, callId, `id ai { summary }`).then(
            (call) => ({
              activityId: a.id,
              summary: call?.ai?.summary ?? ``,
            }),
          )
        }),
      ).then((results) => {
        if (cancelled) return
        const next: Record<string, string> = {}
        for (const r of results) if (r.summary) next[r.activityId] = r.summary
        setCallSummaries(next)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [items])

  // Parallel: pull outcome flags for each FollowUp activity. Same shape/cap
  // pattern as the call-summary fetch above. Activity id prefix is `FollowUp-`.
  useEffect(() => {
    const followUpActivities = items.filter((it) => it.type === `FollowUp`).slice(0, 10)
    if (followUpActivities.length === 0) return
    let cancelled = false
    bg(
      Promise.all(
        followUpActivities.map((a) => {
          const followUpId = a.id.startsWith(`FollowUp-`) ? a.id.slice(`FollowUp-`.length) : a.id
          return gqlFetchOne<{
            id: string
            isConnected?: boolean | null
            isScheduled?: boolean | null
            appointmentDate?: string | null
          }>(`followUp`, followUpId, `id isConnected isScheduled appointmentDate`).then((fu) => ({
            activityId: a.id,
            outcome: fu ?? null,
          }))
        }),
      ).then((results) => {
        if (cancelled) return
        const next: typeof followUpOutcomes = {}
        for (const r of results) {
          if (r.outcome)
            next[r.activityId] = {
              isConnected: r.outcome.isConnected,
              isScheduled: r.outcome.isScheduled,
              appointmentDate: r.outcome.appointmentDate,
            }
        }
        setFollowUpOutcomes(next)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [items])

  // Inject call summaries (into description) and follow-up outcome flags so
  // ActivityRow renders them through its existing slots — no special-casing in
  // the render path.
  const enrichedItems = useMemo(() => {
    if (Object.keys(callSummaries).length === 0 && Object.keys(followUpOutcomes).length === 0) {
      return items
    }
    return items.map((item) => {
      const summary = callSummaries[item.id]
      const outcome = followUpOutcomes[item.id]
      if (!summary && !outcome) return item
      return {
        ...item,
        description: summary ?? item.description,
        followUpOutcome: outcome ?? item.followUpOutcome,
      }
    })
  }, [items, callSummaries, followUpOutcomes])

  // Flat list sorted by date — only keep activities we have an icon mapping for.
  // The server already returned them sorted DESC; this filter just drops noise.
  const visible = useMemo(() => {
    return enrichedItems.filter((item) => activityCategory(item.type) !== null).slice(0, 10)
  }, [enrichedItems])

  if (loading) {
    return <div className="text-body-sm text-subtle">Loading recent activity…</div>
  }
  if (visible.length === 0) {
    return <div className="text-body-sm text-subtle">No activity yet</div>
  }

  return (
    <ol className="flex flex-col gap-3">
      {visible.map((item) => (
        <ActivityRow key={item.id} item={item} />
      ))}
    </ol>
  )
}

function ContactInfoCard({
  contactId,
  onOpenContact,
  onCreateFollowUp,
}: {
  contactId: string
  onOpenContact: () => void
  onCreateFollowUp: (contact: ContactInfo) => void
}) {
  const [contact, setContact] = useState<ContactInfo>()
  const [loading, setLoading] = useState(true)
  const [locationName, setLocationName] = useState<string | undefined>()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    // Dodge the broken `contact(id)` resolver (throws INTERNAL_SERVER_ERROR for many
    // contacts that actually exist) by going through the contacts list with an id filter.
    bg(
      gqlFetchList<ContactInfo>(
        `contacts`,
        { search: `id:${contactId}`, pagination: { page: 0, limit: 1 } },
        CONTACT_FIELDS,
      ).then(({ hits }) => {
        if (cancelled) return
        setContact(hits?.[0])
        setLoading(false)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [contactId])

  // Look up the location name once the contact lands. Tiny `locations` list
  // query with an id filter — same pattern we use elsewhere to side-step
  // a broken single-id resolver and keep field selection narrow.
  useEffect(() => {
    if (!contact?.locationId) {
      setLocationName(undefined)
      return
    }
    let cancelled = false
    bg(
      gqlFetchList<{ id: string; name?: string }>(
        `locations`,
        { search: `id:${contact.locationId}`, pagination: { page: 0, limit: 1 } },
        `id name`,
      ).then(({ hits }) => {
        if (cancelled) return
        setLocationName(hits?.[0]?.name)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [contact?.locationId])

  if (loading) {
    return <div className="px-8 py-6 text-body-sm text-subtle">Loading contact…</div>
  }
  if (!contact) {
    return null
  }

  const phone = contact.cellPhone || contact.homePhone || contact.workPhone
  const phoneLabel = contact.cellPhone ? `Cell` : contact.homePhone ? `Home` : contact.workPhone ? `Work` : `Phone`
  // Show lifecycle stage (Lead → Qualified Lead → New Patient Scheduled →
  // Patient → …) rather than lead status. Lifecycle is the dominant pipeline
  // signal in this org's model. Only mid- and end-of-funnel stages get a
  // color; pre-pipeline (Lead / Qualified Lead) stay neutral gray so the
  // colored badges actually mean something.
  //   Lead / Qualified Lead → gray
  //   New Patient Scheduled → blue (in progress toward conversion)
  //   Patient               → green (closed-won equivalent)
  //   anything else         → gray
  const stage = contact.lifecycleStage
  // Used by the meta-line dot rather than a Badge color now — Tailwind bg
  // classes so we can place a small colored circle inline with the text.
  const stageDotClass = (() => {
    switch (stage) {
      case `New Patient Scheduled`:
        return `bg-blue-500`
      case `Patient`:
        return `bg-green-500`
      default:
        return `bg-(--line-strong)`
    }
  })()
  // Chip surface — tinted bg + matching text color so the stage reads as a
  // status tag next to the name. Mirrors the dot color so the visual
  // language is consistent across the card.
  const stageChipClass = (() => {
    switch (stage) {
      case `New Patient Scheduled`:
        return `bg-blue-50 text-blue-700`
      case `Patient`:
        return `bg-green-50 text-green-800`
      default:
        return `bg-surface-muted text-default`
    }
  })()

  return (
    // `shrink-0` keeps the section from being squeezed by the parent
    // TaskDetailPane's flex-col. Without it, when the activity timeline gets
    // tall, flex-shrink combined with `overflow-hidden` clips the bottom
    // activities — looks like "can't scroll" because content was being
    // hidden instead of overflowing the scroll container.
    <section className="shrink-0 overflow-hidden rounded-lg border border-subtle bg-surface-raised shadow-elevation-1">
      {/* Identity + actions. Single row at wide viewports — at narrow widths
          the action buttons wrap to a second row instead of overlapping the
          name/badge/phone (which would happen with shrink-0 buttons + a
          flex-1 identity column without wrapping). */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-4">
        {/* Drop the bg/text overrides — NameIcon's defaults are
            `bg-blue-100` + `text-blue-600 font-bold`, which is the standard
            contact-initial treatment used elsewhere in the app. */}
        <NameIcon fullName={contact.name?.[0] ?? `?`} heightClassName="h-10" widthClassName="w-10" />
        {/* `min-w-[200px]` keeps the identity column wide enough that
            name/badge/phone stay readable; below that threshold the row's
            `flex-wrap` kicks in and the actions drop to a second line. */}
        <div className="flex min-w-[200px] flex-1 flex-col gap-0.5">
          {/* Row 1 — name + status chip. Status is promoted next to the
              name (where users scan first) so the "stage in pipeline" is
              the first signal, not buried in a generic meta line. The
              chip is a dot + label with a colored background tint matching
              the lifecycle dot color so it reads as a tag, not text. */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
            <h3 className="truncate text-label-md font-semibold leading-tight text-strong">
              {contact.name || `Contact`}
            </h3>
            {stage && (
              <span
                className={classNames(
                  `inline-flex h-5 items-center gap-1.5 rounded-md px-1.5 text-label-xs font-medium`,
                  stageChipClass,
                )}
              >
                <span className={classNames(`h-1.5 w-1.5 shrink-0 rounded-full`, stageDotClass)} />
                {stage}
              </span>
            )}
          </div>
          {/* Row 2 — secondary contact info (phone · location), each
              prefixed with a muted icon. */}
          {(phone || locationName) && (
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-body-xs leading-tight text-subtle">
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="inline-flex items-center gap-1 tabular-nums hover:text-info-default"
                >
                  <PhoneOutcome className="h-3 w-3 shrink-0 text-minimal" strokeWidth={2} />
                  {formatPhone(phone)}
                </a>
              )}
              {locationName && (
                <span className="inline-flex min-w-0 items-center gap-1">
                  {/* `CompaniesIcon` (building) is the app-wide convention
                      for "location" — same icon used on the analytics
                      LocationSelector and other location pickers. */}
                  <CompaniesIcon className="h-3 w-3 shrink-0 text-minimal" />
                  <span className="truncate">{locationName}</span>
                </span>
              )}
            </div>
          )}
        </div>
        {/* `ml-auto` pushes the action group to the right when there's room
            on the same row as the identity column. When the row wraps (narrow
            viewport), ml-auto has no extra space to absorb and the actions
            naturally start at the left of the new row. */}
        <div className="flex w-full items-center gap-1.5 md:ml-auto md:w-auto md:shrink-0">
          <button
            type="button"
            onClick={() => {
              if (!phone) return
              // Wrap in a local OutboundCallProvider in case the app-level provider
              // isn't mounted in this layout — the modal manages its own call state.
              showModal(
                <OutboundCallProvider>
                  <CallInitiationModal contact={contact as ContactJson} />
                </OutboundCallProvider>,
                {
                  title: `Outbound Call`,
                  isFullScreen: false,
                  removePadding: true,
                  closable: true,
                  useRadix: true,
                },
              )
            }}
            disabled={!phone}
            className={classNames(
              // Blue secondary (bordered, white bg, blue text) — matches the
              // Mark complete pattern in the task hero and signals "primary
              // contact action" without dominating the row with a filled blue.
              `inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border px-3 text-ui-xs font-medium transition-colors md:flex-none md:justify-start`,
              phone
                ? `border-blue-600 bg-surface-raised text-blue-600 hover:bg-blue-50`
                : `cursor-not-allowed border-default bg-surface-subtle text-minimal`,
            )}
            title={phone ? `Call ${formatPhone(phone)}` : `No phone on file`}
          >
            <PhoneOutcome className="h-3.5 w-3.5" strokeWidth={2} />
            {phone ? `Call` : `No phone`}
          </button>
          <button
            type="button"
            onClick={() => onCreateFollowUp(contact)}
            className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-default bg-surface-raised px-3 text-ui-xs text-default transition-colors hover:bg-surface-subtle md:flex-none md:justify-start"
            title="Create follow-up"
          >
            {/* Reply icon matches the canonical follow-up icon shape used
                by `FollowUpCard` and the activity timeline rows above. Color
                inherits from the button's `text-trn-700` so the action chip
                reads as neutral chrome — the colored green treatment is
                reserved for the activity feed row's tinted-bg badge. */}
            <Reply className="h-3.5 w-3.5" strokeWidth={2} />
            Follow-up
          </button>
          <button
            type="button"
            onClick={onOpenContact}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-default bg-surface-raised text-subtle transition-colors hover:bg-surface-subtle hover:text-strong"
            title="Open profile"
            aria-label="Open profile"
          >
            <OpenInWindow className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Recent activity — flat list sorted by date (server returns newest-first). */}
      <div className="border-t border-subtle px-5 py-4">
        <h4 className="mb-3 text-body-sm font-semibold text-strong">Activity</h4>
        <ContactActivityTimeline contactId={contactId} />
      </div>
    </section>
  )
}

export function TaskDetailPane({
  task,
  onCheck,
  onClose,
  onDelete,
  onOpenContact,
  onCreateFollowUp,
  onUpdate,
}: {
  task: TaskJson
  onCheck: () => void
  /**
   * Optional. When provided, an X close button renders next to the Mark
   * complete button. Only the Sheet/drawer caller passes this; the split-pane
   * Inbox view leaves it undefined so no close button shows up.
   */
  onClose?: () => void
  /**
   * Called after the user confirms deletion. The caller is responsible for
   * actually invoking the `deleteTask` mutation (so it can optimistically
   * remove the row from its local list state). Returns true on success so
   * we can show the toast + close the drawer.
   */
  onDelete?: (taskId: string) => Promise<boolean>
  onOpenContact: () => void
  onCreateFollowUp: (contact: ContactInfo) => void
  onUpdate: OnTaskUpdate
}) {
  const isClosed = task.status === `closed`
  const isOverdue = !isClosed && !!task.dueDate && dueDateAsDay(task.dueDate).isBefore(moment().startOf(`day`))
  const [confirm] = useConfirmation()
  const ownerFetcher = useMemo(
    () =>
      ({ query }: { query: string }) =>
        assigneesFetcher({ query, locationId: task.locationId, workspaceId: task.workspaceId }).then((result) => ({
          hits: result.hits.map((hit) =>
            hit.kind === `role`
              ? {
                  id: hit.id,
                  name: hit.name,
                  group: `Roles`,
                  disabled: hit.disabled,
                  subtitle: hit.disabled ? `No one at this location` : undefined,
                  icon: <Group className="h-5 w-5 shrink-0 text-subtle" strokeWidth={1.75} />,
                }
              : { id: hit.id, name: hit.name, email: hit.email, group: `People` },
          ),
        })),
    [task.locationId, task.workspaceId],
  )

  const handleDelete = async () => {
    if (!onDelete || !task.id) return
    try {
      // `useConfirmation` resolves on submit, rejects on close. Wrap in
      // try/catch so cancel is a silent no-op.
      await confirm({
        title: `Delete task?`,
        description: `This will delete "${task.title || `this task`}". It can't be undone.`,
        submitLabel: `Delete`,
        cancelLabel: `Cancel`,
        variant: `danger`,
      })
    } catch {
      // User cancelled the confirmation dialog — silent no-op by design.
      log(`tasks: delete cancelled`)
      return
    }
    const ok = await onDelete(task.id)
    if (!ok) return
    notifySuccess(`Task deleted`)
    onClose?.()
  }

  return (
    // No internal scroll — the parent Sheet / inbox right pane handles it.
    // `min-h-full` makes the pane fill its parent's height so the sticky
    // keyboard-hint footer at the bottom always sits at the viewport edge,
    // even when content is short. With tall content, min-h-full is the
    // floor (content height wins) so the footer still sticks via
    // `sticky bottom-0 mt-auto`. No bg — the parent provides its surface.
    // In the inbox split-pane (no `onClose`) the top stays flush so the hero
    // card's top edge aligns with the selector rail beside it. In the Sheet
    // drawer there's no rail to align to, so the card gets the same 12px above
    // it as the gap separating it from the contact card below.
    <div className={classNames(`flex min-h-full w-full flex-col gap-3 px-3 pb-3`, onClose && `pt-3`)}>
      {/* Hero card — title + status pill + due chip + owner chip + contact chip + actions.
          Replaces the old "Header" + "Details" pair. Modeled after Attio / ClickUp / Asana
          where task metadata is a row of clickable chips, not a label/value form. */}
      {/* `shrink-0` so the hero card keeps its content height — flex-col
          default is shrink:1, which combined with the ContactInfoCard's
          `overflow-hidden` was eating the bottom of long activity lists. */}
      <div className="shrink-0 rounded-lg border border-subtle bg-surface-raised px-6 py-5 shadow-elevation-1">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <InlineText
              value={task.title ?? ``}
              onSave={(next) => onUpdate({ title: next })}
              className={classNames(
                `text-heading-lg leading-tight`,
                isClosed ? `text-minimal line-through` : `text-strong`,
              )}
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Mark complete — green secondary (bordered, light hover) so it
                reads as an action button consistent with the rest of the
                toolbar rather than a primary CTA. Stays green-100 fill once
                the task is completed to communicate the resolved state. */}
            <button
              type="button"
              onClick={onCheck}
              className={classNames(
                `inline-flex h-9 items-center gap-2 rounded-md border px-3.5 text-ui-xs font-medium transition-colors`,
                isClosed
                  ? `border-green-200 bg-green-100 text-green-800 hover:bg-green-200`
                  : `border-green-600 bg-surface-raised text-green-600 hover:bg-green-50`,
              )}
            >
              <CheckCircleSolid className="h-4 w-4" />
              {isClosed ? `Completed` : `Mark complete`}
            </button>
            {/* Delete (trash) — destructive secondary, confirmation dialog
                before invoking `deleteTask`. Only renders when an onDelete
                callback is wired (caller owns the mutation + optimistic
                list-state update). */}
            {onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-default bg-surface-raised text-subtle transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                title="Delete task"
                aria-label="Delete task"
              >
                <Trash className="h-4 w-4" strokeWidth={2} />
              </button>
            )}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-default bg-surface-raised text-subtle hover:bg-surface-subtle hover:text-strong"
                title="Close"
                aria-label="Close"
              >
                <Xmark className="h-4 w-4" strokeWidth={2} />
              </button>
            )}
          </div>
        </div>

        {/* Merged details + description — single label/value grid. Status / Due / Owner /
            Contact each occupy one row. Description spans both columns as the last
            "row" with the always-on WYSIWYG below the label. */}
        <div className="mt-6 grid grid-cols-[120px_minmax(0,1fr)] items-center gap-x-6 gap-y-4">
          <div className="text-body-sm text-subtle">Status</div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Status reflects the persisted value only. Rendered as a bordered
                button (dot + label + chevron) rather than a badge so it visually
                reads as an interactive control / select. The Badge primitive
                is reserved for the Overdue modifier next to it. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="group inline-flex h-7 items-center gap-2 rounded-md border border-default bg-surface-raised pl-2 pr-1.5 text-body-sm text-strong shadow-elevation-1 transition-colors hover:border-strong hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-default/30"
                  aria-label="Change status"
                >
                  <span className={classNames(`h-1.5 w-1.5 rounded-full`, isClosed ? `bg-green-600` : `bg-blue-600`)} />
                  <span className="leading-none">{isClosed ? `Complete` : `Open`}</span>
                  <NavArrowDown
                    className="h-3.5 w-3.5 text-minimal transition-colors group-hover:text-subtle"
                    strokeWidth={2}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[160px]">
                <DropdownMenuItem
                  onSelect={() => {
                    if (isClosed) onCheck()
                  }}
                  className="gap-2 text-body-sm"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                  <span className="flex-1">Open</span>
                  {!isClosed && <CheckCircleSolid className="h-3.5 w-3.5 text-minimal" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    if (!isClosed) onCheck()
                  }}
                  className="gap-2 text-body-sm"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
                  <span className="flex-1">Complete</span>
                  {isClosed && <CheckCircleSolid className="h-3.5 w-3.5 text-minimal" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Overdue indicator lives on the Due row (red date text) — no
                need to also render it inline with Status here. */}
          </div>

          <div className="text-body-sm text-subtle">Due</div>
          <div className="min-w-0">
            <InlineDate
              value={task.dueDate}
              // Neutralize the InlineDate wrapper's own padding/hover so the
              // bordered chip inside is the only visual click target.
              className="!m-0 !p-0 hover:!bg-transparent"
              display={
                // `min-w-0 max-w-full` on the chip + `truncate` on the inner
                // label so the date clips with an ellipsis at narrow widths
                // instead of wrapping across multiple lines.
                <span
                  className={classNames(
                    `inline-flex h-7 min-w-0 max-w-full items-center gap-2 rounded-md border border-default bg-surface-raised px-2 text-body-sm shadow-elevation-1 transition-colors hover:border-strong hover:bg-surface-subtle`,
                    isOverdue && !isClosed ? `font-medium text-red-600` : `text-strong`,
                  )}
                >
                  <Calendar
                    className={classNames(
                      `h-3.5 w-3.5 shrink-0`,
                      isOverdue && !isClosed ? `text-red-500` : `text-minimal`,
                    )}
                    strokeWidth={2}
                  />
                  <span className="min-w-0 truncate leading-none">
                    {task.dueDate ? dueDateAsDay(task.dueDate).format(`MMMM D, YYYY`) : `Set due date`}
                  </span>
                </span>
              }
              onSave={(iso) => onUpdate({ dueDate: iso })}
            />
          </div>

          <div className="text-body-sm text-subtle">Owner</div>
          <div className="min-w-0">
            <InlineAsyncPicker
              entity="users"
              fetcher={ownerFetcher}
              onSave={(id) => onUpdate(assigneeInput(id))}
              searchPlaceholder="Search roles and people…"
              triggerClassName="group inline-flex h-7 min-w-0 max-w-full items-center gap-1.5 rounded-md border border-default bg-surface-raised pl-1.5 pr-1.5 text-body-sm text-strong shadow-elevation-1 transition-colors hover:border-strong hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-default/30"
              display={
                <>
                  {task.ownerId || task.roleId ? (
                    // Same `lg:` gate on the avatar + `gap-1.5` as the Contact
                    // trigger so the owner name has the full chip width to
                    // truncate against at narrow viewports.
                    <span className="flex min-w-0 items-center text-body-sm text-strong [&>div]:flex [&>div]:min-w-0 [&>div]:!p-0 lg:[&>div]:gap-1.5 [&>div>div]:hidden lg:[&>div>div]:flex [&_span]:min-w-0 [&_span]:truncate [&_span]:font-normal">
                      <TaskAssigneeLabel ownerId={task.ownerId} roleId={task.roleId} />
                    </span>
                  ) : (
                    <span className="min-w-0 truncate text-body-sm text-minimal">Unassigned</span>
                  )}
                  <NavArrowDown
                    className="ml-auto h-3.5 w-3.5 shrink-0 text-minimal transition-colors group-hover:text-subtle"
                    strokeWidth={2}
                  />
                </>
              }
            />
          </div>

          {isClosed && task.completedByUserId && (task.roleId || task.completedByUserId !== task.ownerId) && (
            <>
              <div className="text-body-sm text-subtle">Completed by</div>
              <div className="min-w-0 text-body-sm text-strong [&>div]:min-w-0 [&_span]:truncate [&_span]:font-normal">
                <LazyStaffCell userId={task.completedByUserId} loadingIndicator={false} />
              </div>
            </>
          )}

          <div className="text-body-sm text-subtle">Contact</div>
          <div className="min-w-0">
            <InlineAsyncPicker
              entity="contacts"
              onSave={(id) => onUpdate({ contactId: id ?? undefined })}
              searchPlaceholder="Search contacts…"
              triggerClassName="group inline-flex h-7 min-w-0 max-w-full items-center gap-1.5 rounded-md border border-default bg-surface-raised pl-1.5 pr-1.5 text-body-sm text-strong shadow-elevation-1 transition-colors hover:border-strong hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-default/30"
              display={
                <>
                  {task.contactId ? (
                    // Hide the avatar `NameIcon` inside `LazyContactCell` only
                    // below `lg` viewport. At narrow widths the avatar ate the
                    // available room and forced the contact name to truncate
                    // to a single character ("C…"). At ≥lg there's room for
                    // both, so we show it. The avatar always appears in the
                    // larger contact info card below regardless.
                    // `gap-1.5` only applies when the avatar is visible (lg+)
                    // so the chip doesn't have a hanging gap at narrow widths.
                    <span className="flex min-w-0 items-center text-body-sm text-strong [&>div]:flex [&>div]:min-w-0 [&>div]:!p-0 lg:[&>div]:gap-1.5 [&>div>div]:hidden lg:[&>div>div]:flex [&_span]:min-w-0 [&_span]:truncate [&_span]:font-normal">
                      <LazyContactCell contactId={task.contactId} loadingIndicator={false} />
                    </span>
                  ) : (
                    <span className="text-body-sm text-minimal">No contact</span>
                  )}
                  <NavArrowDown
                    className="ml-auto h-3.5 w-3.5 shrink-0 text-minimal transition-colors group-hover:text-subtle"
                    strokeWidth={2}
                  />
                </>
              }
            />
          </div>

          {/* Match the muted-gray treatment used by every other field label
              (Status / Due / Owner / Contact) so the row labels read as a
              consistent column rather than Description shouting on its own. */}
          <div className="col-span-2 mt-2 text-body-sm text-subtle">Description</div>
          <div className="col-span-2">
            <AutoSaveRichText
              key={`task-${task.id}-desc`}
              namespace={`task-${task.id}-desc`}
              value={task.description ?? ``}
              onSave={(next) => onUpdate({ description: next || null })}
              placeholder="Add a description…"
            />
          </div>
        </div>
      </div>

      {task.contactId && (
        <ContactInfoCard contactId={task.contactId} onOpenContact={onOpenContact} onCreateFollowUp={onCreateFollowUp} />
      )}

      {/* Keyboard hints — only rendered for the drawer case (where `onClose`
          is wired). The inbox split-pane gets its hints on the selector rail
          instead so they're not duplicated.
          `mt-auto` consumes leftover flex-col space so the hints sit at the
          bottom of TaskDetailPane (which has min-h-full → parent height).
          `sticky bottom-0` then pins them to the scroll viewport's bottom
          edge no matter how far the user has scrolled. */}
      {onClose && (
        <div className="sticky bottom-0 -mx-3 -mb-3 mt-auto hidden flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t border-default/60 bg-surface-subtle/85 px-3 py-2 text-body-xs text-subtle backdrop-blur-sm md:flex">
          <span className="inline-flex items-center gap-1.5">
            <KeyboardKey>↑</KeyboardKey>
            <KeyboardKey>↓</KeyboardKey>
            navigate
          </span>
          <span className="inline-flex items-center gap-1.5">
            <KeyboardKey>X</KeyboardKey>
            complete
          </span>
          <span className="inline-flex items-center gap-1.5">
            <KeyboardKey>Esc</KeyboardKey>
            close
          </span>
        </div>
      )}
    </div>
  )
}

function KeyboardKey({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-[20px] items-center justify-center rounded border border-default bg-surface-raised px-1 font-mono text-[10px] font-medium text-subtle shadow-elevation-1">
      {children}
    </kbd>
  )
}
